import { useSelector } from "react-redux";
import { selectUpdatePackageId } from "../../features/client/clientSlice";
import { useDynamicQueryData } from "../../hooks/api/useDynamicQueryData";
import { useEffect, useMemo } from "react";
import { ProductSummary } from "../../features/package/packageInterface";
import { showToast } from "../../toastify/Toast";
import Button from "../ui/Button";
import Loading from "../ui/Loading";
import ErrorMessage from "../ui/ErrorMessage";
import EmptyData from "../ui/EmptyData";

interface ProductAvailableProps {
    products: ProductSummary[]
    showAddProductSidebar: boolean;
    setShowAddProductSidebar: (value: boolean) => void;
    addProducts: (value: ProductSummary[]) => void;
}

export default function ProductAvailable({ addProducts, products, showAddProductSidebar, setShowAddProductSidebar }: ProductAvailableProps) {
    
    const packageID = useSelector(selectUpdatePackageId);
    const { getPackageProducts, isErrorGetPackageProducts, refetchGetPackageProducts, isLoadingGetPackageProducts, errorMessageGetPackageProducts, } = useDynamicQueryData({packageID})

    useEffect(() => {
        if (showAddProductSidebar) {
            refetchGetPackageProducts();
        }
    }, [showAddProductSidebar, refetchGetPackageProducts]);

    const activeProducts = useMemo(() => {
        // return getPackageProducts?.items?.filter(product => product.isActive) ?? [];
        return getPackageProducts?.items ?? [];
    }, [getPackageProducts, showAddProductSidebar]);

    const handleToggleProduct = (prod: typeof activeProducts[number]) => {
        const existingProduct = products.find( p => p.productID === prod.productID);

        // Not selected yet -> Add
        if (!existingProduct) {
            const productToAdd: ProductSummary = {
                packageID: packageID ?? 0,
                productID: prod.productID,
                productName: prod.productName,
                price: prod.price,
                sizeId: prod.sizeId,
                size: prod.size,
                categoryID: prod.categoryID,
                categoryName: prod.categoryName,
                isAvailable: prod.isAvailable,
                isActive: true,
                type: "new",
                addons: [...(prod.addons ?? [])],
                typeName: prod.typeName,
            };
            addProducts([...products, productToAdd]);
            return;
        }

        // Existing product from database -> Don't allow remove
        if (existingProduct.type === "old") {
            return showToast(
                "This product is already included in the package and cannot be removed.",
                "warning"
            );
        }

        // Newly added -> Remove it
        addProducts( products.filter(p => p.productID !== prod.productID));
    };

    const newSelectedCount = useMemo(() => {
        return products.filter(product => product.type === "new").length;
    }, [products]);

    const handleClearNewProducts = () => {
        addProducts(
            products.filter(product => product.type !== "new")
        );
    };

    return (
        <div className={` ${showAddProductSidebar ? "translate-x-0" : "translate-x-full"} absolute top-0 right-0 h-full w-[50%] bg-white border-l shadow-xl z-20 transition-transform duration-300 ease-in-out flex flex-col`}>
        {/* <div className={`absolute top-0 right-0 h-full w-[50%] bg-white border-l shadow-xl z-20 transition-transform duration-300 ease-in-out flex flex-col`}> */}
            <div className="flex items-center justify-between p-3 border-b flex-shrink-0">
            <div>
                <h2 className="font-semibold text-sm">Add Product</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                    Select a product to add or remove.
                </p>
            </div>
            <Button
                onClick={() => setShowAddProductSidebar(false)}
                className="px-8 py-1 text-xs rounded bg-cbColor text-white"
                btnName="Done"
            />
        </div>
            <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
                <p className="text-xs text-gray-600">
                    {newSelectedCount} new product{newSelectedCount !== 1 ? "s" : ""} selected
                </p>
                {newSelectedCount > 0 && (
                    <button
                        onClick={handleClearNewProducts}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                    >
                        Clear Selection
                    </button>
                )}
            </div>
            <div className="flex-1 overflow-y-auto p-2.5">
                {isLoadingGetPackageProducts ? (
                    <div className='flex justify-center items-center h-full'>
                        <Loading message="Loading products..." fontSize="text-sm" />
                    </div>
                ) : isErrorGetPackageProducts ? (
                    <div className='flex justify-center items-center h-full'>
                        <ErrorMessage message={errorMessageGetPackageProducts} textStyle='text-xs' />
                    </div>
                ) : (
                    <div className="space-y-3">
                        {activeProducts.map((prod) => {
                            const addons = prod.addons ?? []
                            const selectedProduct = products.find(p => p.productID === prod.productID);
                            const isSelected = !!selectedProduct;
                            return (
                            <div
                                key={prod.productID}
                                onClick={() => handleToggleProduct(prod)}
                                className={`
                                    border rounded-lg px-3 py-2 transition-all duration-200
                                    ${ !isSelected ? "border-gray-200 hover:bg-blue-50 hover:border-green-500 cursor-pointer"
                                        : selectedProduct.type === "new" ? "border-green-500 bg-green-50 cursor-pointer hover:bg-red-50 hover:border-red-400"
                                        : "border-cbColor bg-blue-50 cursor-not-allowed"
                                    }
                                `}
                            >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">
                                        {prod.productName}
                                    </p>
                                    <p className="text-xs text-gray-700">
                                        {prod.size} • {prod.categoryName} 
                                    </p>
                                </div>
                            </div>
                            {addons.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-gray-200">
                                    <p className="text-xs font-medium text-gray-500 mb-2">
                                        Add-ons
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                        {addons.map((addon) => (
                                            <span
                                                key={addon.addonID}
                                                className="px-2 py-0.5 rounded bg-gray-100 border border-gray-200 text-xs text-gray-700"
                                            >
                                                {addon.addonName}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            </div>
                        )})}
                    </div>
                )}
            </div>
        </div>
    )
}
